import ServiceLayout from "@/components/ServiceLayout";
import { QuoteModal } from "@/components/QuoteModal";
import { Button } from "@/components/ui/button";
import { Phone } from "lucide-react";

export default function Lichtinstallation() {
  return (
    <ServiceLayout
      title="Lichtinstallation in Augsburg"
      description="Professionelle Lichtinstallation für sichere Anschlüsse, moderne Beleuchtung und ein stimmiges Raumgefühl – präzise umgesetzt und sauber montiert."
      image="https://images.leadconnectorhq.com/image/f_webp/q_80/r_1200/u_https://assets.cdn.filesafe.space/Zrqvy6cNzSbnzDV55cv1/media/69370c2d2ec4f5960a9a42f1.jpg"
      content={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight">
            Beleuchtung, die Atmosphäre schafft
          </h2>
          <p className="text-lg text-neutral-600">
            Das richtige Licht ist entscheidend für das Wohlbefinden in den eigenen vier Wänden. Wir übernehmen die fachgerechte Installation Ihrer Lampen, Leuchten und komplexen Lichtsysteme. Von der einfachen Deckenlampe bis hin zur indirekten LED-Beleuchtung.
          </p>
          <p className="text-lg text-neutral-600">
            Besonders nach einem Umzug ist eine schnelle und sichere Montage der Beleuchtung wichtig. Unsere Experten sorgen dafür, dass alle Anschlüsse sicher verlegt und Ihre Lampen perfekt positioniert sind, um Ihre Räume optimal auszuleuchten.
          </p>
          <ul className="space-y-3 mt-6">
            {[
              "Montage von Decken- und Wandleuchten",
              "Installation von LED-Spots und Stripes",
              "Sicherer Anschluss ans Stromnetz",
              "Beratung zur optimalen Raumausleuchtung"
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3 text-neutral-800 font-medium">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>
                </div>
                {item}
              </li>
            ))}
          </ul>
        </>
      }
      extendedContent={
        <div className="bg-white">
          <section className="py-16 bg-neutral-50">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Professionelle Lichtinstallation in Augsburg – Für ein strahlendes Zuhause</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Licht ist weit mehr als nur Helligkeit. Es beeinflusst unsere Stimmung, unsere Produktivität und maßgeblich die Atmosphäre eines Raumes. Eine gut geplante und professionell ausgeführte Lichtinstallation ist daher ein wesentlicher Bestandteil einer jeden Wohnungseinrichtung. Als Ihr kompetenter Partner in Augsburg kümmern wir uns um die sichere und ästhetische Montage Ihrer Beleuchtung. Ob beim Einzug in eine neue Wohnung, nach einer Renovierung oder bei der Neugestaltung einzelner Räume – wir bringen Licht ins Dunkel.
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Das Aufhängen und Anschließen von Lampen gehört zu den Aufgaben, die beim Umzug oft als letztes erledigt werden, aber am dringendsten benötigt werden. Nichts ist ungemütlicher, als die ersten Abende im neuen Heim bei Baustellenbeleuchtung zu verbringen. Unser Team montiert Ihre Deckenleuchten, Wandlampen, Kronleuchter und modernen LED-Systeme zügig und sicher, sodass Sie sich von der ersten Minute an zu Hause fühlen.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Kostenloses Angebot anfordern
                      </Button>
                    </QuoteModal>
                    <Button size="lg" variant="secondary" asChild className="w-full sm:w-auto">
                      <a href="tel:+4915888640762">
                        <Phone className="w-5 h-5 mr-2" />
                        +49 15888 640762
                      </a>
                    </Button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 pt-8">
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Sicherheit beim Stromanschluss</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Die Arbeit an elektrischen Leitungen birgt immer Gefahren. Fehlende Erdung, falsche Phasenanschlüsse oder überlastete Stromkreise können zu Kurzschlüssen oder gar Bränden führen. Unsere geschulten Monteure kennen die Sicherheitsvorschriften genau. Wir prüfen vor jeder Installation die vorhandenen Leitungen auf Spannungsfreiheit und sorgen für einen fachgerechten und VDE-konformen Anschluss all Ihrer Leuchten.
                    </p>
                  </div>
                  <div className="bg-white p-8 border border-neutral-200">
                    <h3 className="font-heading text-xl font-bold mb-3">Moderne LED- und Smart-Home-Systeme</h3>
                    <p className="text-neutral-600 leading-relaxed">
                      Die Beleuchtungstechnik hat sich in den letzten Jahren rasant entwickelt. Wir installieren nicht nur klassische Hängelampen, sondern auch komplexe Schienensysteme, Einbauspots in abgehängten Decken und indirekte LED-Stripes für eine stimmungsvolle Hintergrundbeleuchtung. Auf Wunsch binden wir Ihre neuen Lampen auch in bestehende Smart-Home-Systeme ein, damit Sie Ihr Licht bequem per App oder Sprachbefehl steuern können.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto space-y-8">
                <div>
                  <h2 className="font-heading text-3xl font-bold mb-4">Schwere Kronleuchter und empfindliche Designerlampen</h2>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Besondere Leuchten erfordern eine besondere Behandlung. Ein schwerer Kristall-Kronleuchter im Wohnzimmer oder Treppenhaus kann nicht einfach an einem Standard-Haken in der Trockenbaudecke befestigt werden. Wir prüfen die Tragfähigkeit der Decke und verwenden spezielle Schwerlastdübel und Verankerungen, um eine absolute Sturzsicherheit zu garantieren. 
                  </p>
                  <p className="text-neutral-600 leading-relaxed mb-4">
                    Auch bei hochwertigen und empfindlichen Designerlampen gehen wir mit äußerster Vorsicht vor. Wir tragen bei der Montage spezielle Handschuhe, um keine Fingerabdrücke auf empfindlichen Chrom- oder Glasoberflächen zu hinterlassen, und justieren die Pendelhöhe exakt nach Ihren Wünschen über dem Esstisch aus.
                  </p>
                  <div className="pt-4 pb-2 flex flex-col sm:flex-row items-start gap-4">
                    <QuoteModal>
                      <Button size="lg" className="font-bold text-base px-8 py-6 rounded-none bg-primary text-white hover:bg-primary/90 w-full sm:w-auto">
                        Jetzt beraten lassen
                      </Button>
                    </QuoteModal>
                  </div>
                </div>

                <div className="bg-primary/5 p-8 border-l-4 border-primary mt-8">
                  <h3 className="font-heading text-2xl font-bold mb-3">Bohren ohne böse Überraschungen</h3>
                  <p className="text-neutral-700 leading-relaxed">
                    Das Bohren in Decken und Wände birgt immer das Risiko, versteckte Strom- oder Wasserleitungen zu treffen. Bevor wir den Bohrer ansetzen, scannen wir den Montagebereich mit professionellen Leitungssuchgeräten. So schließen wir teure und gefährliche Schäden von vornherein aus. Zudem arbeiten wir mit Bohrstaubabsaugung, sodass Ihre Möbel und Böden während der Installation sauber bleiben.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 bg-neutral-950 text-white">
            <div className="container mx-auto px-4 md:px-6">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="font-heading text-3xl font-bold mb-6">Darum Easy Umzug für Ihr Licht</h2>
                <div className="grid sm:grid-cols-3 gap-8 mt-12 text-left">
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Maximale Sicherheit</h3>
                    <p className="text-neutral-400">Fachgerechter Anschluss nach VDE-Normen schützt vor Kurzschlüssen und Kabelbränden.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Präzise Montage</h3>
                    <p className="text-neutral-400">Exakte Ausrichtung über Tischen und sichere Verankerung auch bei schweren Leuchten.</p>
                  </div>
                  <div>
                    <div className="w-12 h-12 bg-primary flex items-center justify-center mb-4">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>
                    </div>
                    <h3 className="font-bold text-lg mb-2">Sauberes Arbeiten</h3>
                    <p className="text-neutral-400">Leitungssuche vor dem Bohren und direkte Staubabsaugung schützen Ihr Inventar.</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      }
    />
  );
}
